import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel, Divider, Select, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Inventory as ProductsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
  AutoAwesome as AutoAwesomeIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { platformApi } from '../api/services';

interface Product {
  id: string;
  name: string;
  description: string | null;
  pricePerSeatPence: number;
  currency: string;
  billingInterval: string;
  maxExecutiveSeats: number;
  isPopular: boolean;
  isCustomPricing: boolean;
  stripeProductId?: string;
  stripePriceId?: string;
  aiPlanningEnabled: boolean;
  transcriptProcessingEnabled: boolean;
  imageAnalysisEnabled: boolean;
  pdfExportEnabled: boolean;
  webhooksEnabled: boolean;
  apiAccessEnabled: boolean;
  ssoEnabled: boolean;
  customBrandingEnabled: boolean;
  crossExecutiveReportingEnabled: boolean;
  prioritySupportEnabled: boolean;
  dedicatedOnboardingEnabled: boolean;
}


const FEATURE_FLAGS: { key: keyof Product; label: string }[] = [
  { key: 'aiPlanningEnabled', label: 'AI Planning' },
  { key: 'transcriptProcessingEnabled', label: 'Transcript Processing' },
  { key: 'imageAnalysisEnabled', label: 'Image Analysis' },
  { key: 'pdfExportEnabled', label: 'PDF Export' },
  { key: 'webhooksEnabled', label: 'Webhooks' },
  { key: 'apiAccessEnabled', label: 'API Access' },
  { key: 'ssoEnabled', label: 'SSO' },
  { key: 'customBrandingEnabled', label: 'Custom Branding' },
  { key: 'crossExecutiveReportingEnabled', label: 'Cross-Executive Reporting' },
  { key: 'prioritySupportEnabled', label: 'Priority Support' },
  { key: 'dedicatedOnboardingEnabled', label: 'Dedicated Onboarding' },
];

const emptyProduct: Omit<Product, 'id'> = {
  name: '',
  description: '',
  pricePerSeatPence: 0,
  currency: 'GBP',
  billingInterval: 'Monthly',
  maxExecutiveSeats: 1,
  isPopular: false,
  isCustomPricing: false,
  stripeProductId: '',
  stripePriceId: '',
  aiPlanningEnabled: false,
  transcriptProcessingEnabled: false,
  imageAnalysisEnabled: false,
  pdfExportEnabled: false,
  webhooksEnabled: false,
  apiAccessEnabled: false,
  ssoEnabled: false,
  customBrandingEnabled: false,
  crossExecutiveReportingEnabled: false,
  prioritySupportEnabled: false,
  dedicatedOnboardingEnabled: false,
};

function formatPrice(pence: number, currency: string): string {
  const amount = pence / 100;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export default function PlatformProductsPage() {
  const theme = useTheme();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<{ id: string; type: 'success' | 'error'; text: string } | null>(null);

  // Stripe settings state
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripeLiveMode, setStripeLiveMode] = useState(false);
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [stripeSaveLoading, setStripeSaveLoading] = useState(false);
  const [stripeSaveMessage, setStripeSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // AI settings state
  const [aiConfigured, setAiConfigured] = useState(false);
  const [aiProvider, setAiProvider] = useState('Anthropic');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [anthropicModel, setAnthropicModel] = useState('claude-sonnet-4-5-20250514');
  const [openAiKey, setOpenAiKey] = useState('');
  const [openAiModel, setOpenAiModel] = useState('gpt-4o');
  const [aiSaveLoading, setAiSaveLoading] = useState(false);
  const [aiSaveMessage, setAiSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await platformApi.getProducts();
      setProducts(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStripeSettings = useCallback(async () => {
    try {
      const data = await platformApi.getStripeSettings();
      setStripeConfigured(data.isConfigured ?? false);
      setStripeLiveMode(data.isLiveMode ?? false);
      if (data.publishableKey) setStripePublishableKey(data.publishableKey);
    } catch {
      // Stripe settings not loaded — non-critical
    }
  }, []);

  const loadAiSettings = useCallback(async () => {
    try {
      const data = await platformApi.getAiSettings();
      setAiConfigured(data.isConfigured ?? false);
      if (data.defaultProvider && data.defaultProvider !== 'None') setAiProvider(data.defaultProvider);
      if (data.anthropicModel) setAnthropicModel(data.anthropicModel);
      if (data.openAiModel) setOpenAiModel(data.openAiModel);
    } catch {
      // AI settings not loaded — non-critical
    }
  }, []);

  useEffect(() => { loadProducts(); loadStripeSettings(); loadAiSettings(); }, [loadProducts, loadStripeSettings, loadAiSettings]);

  const handleSaveStripeSettings = async () => {
    setStripeSaveLoading(true);
    setStripeSaveMessage(null);
    try {
      const payload: Record<string, string> = {};
      if (stripeSecretKey) payload.secretKey = stripeSecretKey;
      if (stripePublishableKey) payload.publishableKey = stripePublishableKey;
      if (stripeWebhookSecret) payload.webhookSecret = stripeWebhookSecret;
      const result = await platformApi.updateStripeSettings(payload);
      setStripeConfigured(result.isConfigured ?? false);
      setStripeLiveMode(result.isLiveMode ?? false);
      setStripeSaveMessage({ type: 'success', text: result.message || 'Stripe settings saved.' });
      setStripeSecretKey('');
      setStripeWebhookSecret('');
      loadStripeSettings();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save Stripe settings';
      setStripeSaveMessage({ type: 'error', text: msg });
    } finally {
      setStripeSaveLoading(false);
    }
  };

  const handleSaveAiSettings = async () => {
    setAiSaveLoading(true);
    setAiSaveMessage(null);
    try {
      const payload: Record<string, string> = { defaultProvider: aiProvider };
      if (anthropicKey) payload.anthropicApiKey = anthropicKey;
      if (anthropicModel) payload.anthropicModel = anthropicModel;
      if (openAiKey) payload.openAiApiKey = openAiKey;
      if (openAiModel) payload.openAiModel = openAiModel;
      const result = await platformApi.updateAiSettings(payload);
      setAiConfigured(result.isConfigured ?? false);
      setAiSaveMessage({ type: 'success', text: result.message || 'AI settings saved.' });
      setAnthropicKey('');
      setOpenAiKey('');
      loadAiSettings();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save AI settings';
      setAiSaveMessage({ type: 'error', text: msg });
    } finally {
      setAiSaveLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingProduct({ ...emptyProduct });
    setIsCreating(true);
    setSaveError(null);
  };

  const handleOpenEdit = (product: Product) => {
    setEditingProduct({ ...product });
    setIsCreating(false);
    setSaveError(null);
  };

  const handleClose = () => {
    setEditingProduct(null);
    setIsCreating(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editingProduct?.name?.trim()) {
      setSaveError('Product name is required');
      return;
    }
    setSaveLoading(true);
    setSaveError(null);
    try {
      if (isCreating) {
        await platformApi.createProduct(editingProduct);
      } else if (editingProduct.id) {
        await platformApi.updateProduct(editingProduct.id, editingProduct);
      }
      handleClose();
      loadProducts();
    } catch (err: any) {
      setSaveError(err.response?.data?.message || err.message || 'Failed to save product');
    } finally {
      setSaveLoading(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setEditingProduct(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleSyncToStripe = async (productId: string) => {
    setSyncingId(productId);
    setSyncMessage(null);
    try {
      const result = await platformApi.syncProductToStripe(productId);
      setSyncMessage({ id: productId, type: 'success', text: result.message || 'Synced to Stripe successfully.' });
      loadProducts();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to sync to Stripe';
      setSyncMessage({ id: productId, type: 'error', text: msg });
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <Box>
      {/* Stripe Configuration */}
      <Card sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Stripe Configuration</Typography>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Chip label={stripeConfigured ? 'Connected' : 'Not Connected'}
                color={stripeConfigured ? 'success' : 'error'} size="small" />
          {stripeLiveMode && <Chip label="Live Mode" color="warning" size="small" />}
          {stripeConfigured && !stripeLiveMode && <Chip label="Test Mode" size="small" />}
        </Box>

        {stripeSaveMessage && (
          <Alert severity={stripeSaveMessage.type} sx={{ mb: 2 }} onClose={() => setStripeSaveMessage(null)}>
            {stripeSaveMessage.text}
          </Alert>
        )}

        <TextField
          label="Secret Key"
          type="password"
          fullWidth
          value={stripeSecretKey}
          onChange={e => setStripeSecretKey(e.target.value)}
          sx={{ mb: 2 }}
          helperText="sk_test_... or sk_live_..."
          placeholder={stripeConfigured ? '(unchanged — enter new value to update)' : ''}
        />
        <TextField
          label="Publishable Key"
          fullWidth
          value={stripePublishableKey}
          onChange={e => setStripePublishableKey(e.target.value)}
          sx={{ mb: 2 }}
          helperText={stripePublishableKey && stripePublishableKey.length > 12
            ? `Current: ${stripePublishableKey.slice(0, 8)}${'*'.repeat(Math.max(0, stripePublishableKey.length - 12))}${stripePublishableKey.slice(-4)}`
            : "pk_test_... or pk_live_..."}
          type="password"
        />
        <TextField
          label="Webhook Secret"
          type="password"
          fullWidth
          value={stripeWebhookSecret}
          onChange={e => setStripeWebhookSecret(e.target.value)}
          sx={{ mb: 2 }}
          helperText="whsec_..."
          placeholder={stripeConfigured ? '(unchanged — enter new value to update)' : ''}
        />
        <Button
          variant="contained"
          onClick={handleSaveStripeSettings}
          disabled={stripeSaveLoading}
        >
          {stripeSaveLoading ? 'Saving...' : 'Save Stripe Settings'}
        </Button>
      </Card>

      {/* AI Provider Settings */}
      <Card sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>AI Provider Settings</Typography>
        </Stack>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Chip label={aiConfigured ? 'Configured' : 'Not Configured'}
                color={aiConfigured ? 'success' : 'default'} size="small" />
          <Chip label={`Default: ${aiProvider}`} size="small" />
        </Box>

        {!aiConfigured && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No API keys saved yet. Enter your Anthropic or OpenAI key to enable AI features.
          </Alert>
        )}

        {aiSaveMessage && (
          <Alert severity={aiSaveMessage.type} sx={{ mb: 2 }} onClose={() => setAiSaveMessage(null)}>
            {aiSaveMessage.text}
          </Alert>
        )}

        <Select
          value={aiProvider}
          onChange={e => setAiProvider(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        >
          <MenuItem value="Anthropic">Anthropic Claude (Recommended)</MenuItem>
          <MenuItem value="OpenAI">OpenAI</MenuItem>
        </Select>

        <TextField
          label="Anthropic API Key"
          type="password"
          fullWidth
          value={anthropicKey}
          onChange={e => setAnthropicKey(e.target.value)}
          sx={{ mb: 2 }}
          helperText="sk-ant-..."
          placeholder={aiConfigured ? '(unchanged — enter new value to update)' : ''}
        />

        <TextField
          label="Anthropic Model"
          fullWidth
          value={anthropicModel}
          onChange={e => setAnthropicModel(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          label="OpenAI API Key (fallback)"
          type="password"
          fullWidth
          value={openAiKey}
          onChange={e => setOpenAiKey(e.target.value)}
          sx={{ mb: 2 }}
          helperText="sk-..."
          placeholder={aiConfigured ? '(unchanged — enter new value to update)' : ''}
        />

        <TextField
          label="OpenAI Model"
          fullWidth
          value={openAiModel}
          onChange={e => setOpenAiModel(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Button
          variant="contained"
          onClick={handleSaveAiSettings}
          disabled={aiSaveLoading}
        >
          {aiSaveLoading ? 'Saving...' : 'Save AI Settings'}
        </Button>
      </Card>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ProductsIcon sx={{ color: 'error.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h4">Products & Pricing</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage billing products and feature flags
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ fontWeight: 600 }}
        >
          Create Product
        </Button>
      </Box>

      {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Product Cards */}
      <Grid container spacing={2.5}>
        {products.map(product => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={product.id}>
            <Card sx={{ bgcolor: 'background.paper', height: '100%', position: 'relative' }}>
              {product.isPopular && (
                <Chip
                  label="Popular"
                  size="small"
                  color="warning"
                  sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 600 }}
                />
              )}
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {product.name}
                    </Typography>
                    {product.description && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {product.description}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    {product.isCustomPricing ? (
                      <Typography variant="h5" sx={{ color: 'warning.main', fontWeight: 700 }}>
                        Custom Pricing
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
                          {formatPrice(product.pricePerSeatPence, product.currency)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          / seat / {product.billingInterval.toLowerCase()}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Up to {product.maxExecutiveSeats} executive seat{product.maxExecutiveSeats !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Feature flags */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                      Features
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {FEATURE_FLAGS.map(flag => {
                        const enabled = Boolean(product[flag.key]);
                        return (
                          <Chip
                            key={flag.key}
                            label={flag.label}
                            size="small"
                            sx={{
                              bgcolor: enabled ? (t: any) => `${t.palette.success.main}1F` : (t: any) => t.palette.action.hover,
                              color: enabled ? 'success.main' : 'text.secondary',
                              fontWeight: enabled ? 600 : 400,
                              fontSize: '0.7rem',
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>

                  {/* Stripe Integration Status */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                      Stripe
                    </Typography>
                    {product.stripeProductId ? (
                      <Stack spacing={0.25}>
                        <Typography variant="caption" sx={{ color: 'success.main', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          Product: {product.stripeProductId}
                        </Typography>
                        {product.stripePriceId && (
                          <Typography variant="caption" sx={{ color: 'success.main', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            Price: {product.stripePriceId}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Not synced
                      </Typography>
                    )}
                    {syncMessage?.id === product.id && (
                      <Alert severity={syncMessage.type} sx={{ mt: 0.5, py: 0, fontSize: '0.75rem' }} onClose={() => setSyncMessage(null)}>
                        {syncMessage.text}
                      </Alert>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenEdit(product)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      startIcon={<SyncIcon />}
                      onClick={() => handleSyncToStripe(product.id)}
                      disabled={syncingId === product.id}
                    >
                      {syncingId === product.id ? 'Syncing...' : 'Sync to Stripe'}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {!loading && products.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">No products configured</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Create your first billing product to get started.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Plan Comparison Matrix */}
      {products.length > 0 && (
        <Card sx={{ bgcolor: 'background.paper', mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CompareIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Plan Comparison
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>
                      Feature
                    </TableCell>
                    {products.map(p => (
                      <TableCell key={p.id} align="center" sx={{ color: 'text.primary', borderColor: 'divider', fontWeight: 600 }}>
                        {p.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Pricing row */}
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 500 }}>
                      Price / seat / month
                    </TableCell>
                    {products.map(p => (
                      <TableCell key={p.id} align="center" sx={{ borderColor: 'divider' }}>
                        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {p.isCustomPricing ? 'Custom' : formatPrice(p.pricePerSeatPence, p.currency)}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* Max seats row */}
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 500 }}>
                      Max executive seats
                    </TableCell>
                    {products.map(p => (
                      <TableCell key={p.id} align="center" sx={{ borderColor: 'divider' }}>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {p.maxExecutiveSeats}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* Feature flag rows */}
                  {FEATURE_FLAGS.map(flag => (
                    <TableRow key={flag.key}>
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 500 }}>
                        {flag.label}
                      </TableCell>
                      {products.map(p => {
                        const enabled = Boolean(p[flag.key]);
                        return (
                          <TableCell key={p.id} align="center" sx={{ borderColor: 'divider' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: enabled ? 'success.main' : 'text.disabled',
                                fontWeight: enabled ? 600 : 400,
                              }}
                            >
                              {enabled ? '\u2713' : '\u2014'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={editingProduct !== null} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isCreating ? 'Create Product' : 'Edit Product'}</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {editingProduct && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Product Name"
                fullWidth
                value={editingProduct.name || ''}
                onChange={e => updateField('name', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={editingProduct.description || ''}
                onChange={e => updateField('description', e.target.value)}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Price (pence per seat)"
                  type="number"
                  fullWidth
                  value={editingProduct.pricePerSeatPence || 0}
                  onChange={e => updateField('pricePerSeatPence', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Currency"
                  select
                  fullWidth
                  value={editingProduct.currency || 'GBP'}
                  onChange={e => updateField('currency', e.target.value)}
                >
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Billing Interval"
                  select
                  fullWidth
                  value={editingProduct.billingInterval || 'Monthly'}
                  onChange={e => updateField('billingInterval', e.target.value)}
                >
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Annual">Annual</MenuItem>
                </TextField>
                <TextField
                  label="Max Executive Seats"
                  type="number"
                  fullWidth
                  value={editingProduct.maxExecutiveSeats || 1}
                  onChange={e => updateField('maxExecutiveSeats', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                />
              </Stack>

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Stripe Integration</Typography>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Stripe Product ID"
                  fullWidth
                  value={(editingProduct as any).stripeProductId || ''}
                  onChange={e => updateField('stripeProductId', e.target.value)}
                  placeholder="prod_..."
                />
                <TextField
                  label="Stripe Price ID"
                  fullWidth
                  value={(editingProduct as any).stripePriceId || ''}
                  onChange={e => updateField('stripePriceId', e.target.value)}
                  placeholder="price_..."
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Switch checked={editingProduct.isPopular || false} onChange={e => updateField('isPopular', e.target.checked)} />}
                  label="Popular"
                />
                <FormControlLabel
                  control={<Switch checked={editingProduct.isCustomPricing || false} onChange={e => updateField('isCustomPricing', e.target.checked)} />}
                  label="Custom Pricing"
                />
              </Stack>

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Feature Flags</Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                {FEATURE_FLAGS.map(flag => (
                  <FormControlLabel
                    key={flag.key}
                    control={
                      <Switch
                        size="small"
                        checked={Boolean(editingProduct[flag.key])}
                        onChange={e => updateField(flag.key, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{flag.label}</Typography>}
                  />
                ))}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleSave} disabled={saveLoading}>
            {saveLoading ? 'Saving...' : isCreating ? 'Create' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
